import { SecureLoader } from "@/components/dashboard/SecureLoader";
import {
  DetailsBuyForm,
  DetailsSellForm,
  OrderSummary,
  SocialProof,
} from "@/components/order-summary";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  buySubmissionFormEventDataSchema,
  sellSubmissionFormEventDataSchema,
} from "@/core";
import { useFingerprint, useGetCustomerById } from "@/hooks";
import { resolveCustomerContact } from "@/lib/customer-contact";
import { serviceHost } from "@/services";
import { useDashboardStore, useUserCredentialsStore } from "@/stores";
import { TransactionTypes } from "@/stores/user-credentials";
import {
  buildCustomerPortalAnalyticsPayload,
  parseFingerprintData,
  pushCustomerPortalAnalyticsEvent,
  savePendingDealSubmittedPayload,
} from "@/utils";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { z } from "zod";

export function OrderSummaryPage() {
  const navigate = useNavigate();
  const functionsService = serviceHost.getFunctionsService();

  const transactionType = useUserCredentialsStore((s) => s.transactionType);
  const { fingerprintData, collectFingerprint } = useFingerprint({
    autoCollect: transactionType === TransactionTypes.SELL,
  });
  const customerId = useUserCredentialsStore((s) => s.customerId);
  const fallbackEmail = useUserCredentialsStore((s) => s.email);
  const fallbackFirstName = useUserCredentialsStore((s) => s.firstName);
  const fallbackLastName = useUserCredentialsStore((s) => s.lastName);
  const fallbackPhoneNumber = useUserCredentialsStore((s) => s.phoneNumber);
  const customerQuery = useGetCustomerById(customerId);
  const customerContact = resolveCustomerContact({
    customer: customerQuery.data,
    fallbackProfile: {
      email: fallbackEmail,
      phoneNumber: fallbackPhoneNumber,
    },
  });
  const isWaitingForCustomerContact =
    transactionType === TransactionTypes.SELL &&
    Boolean(customerId) &&
    !customerQuery.isFetched &&
    (!fallbackEmail || !fallbackPhoneNumber);

  const program = useDashboardStore((s) => s.program);
  const [isFormSubmitting, setIsFormSubmitting] = useState<boolean>(false);
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const formRef = useRef<HTMLDivElement>(null);
  const hasTrackedPageOpenRef = useRef(false);

  const accountDetails = program?.accountDetails;

  useEffect(() => {
    if (hasTrackedPageOpenRef.current || !program) {
      return;
    }

    if (customerId && !customerQuery.isFetched) {
      return;
    }

    const payload = buildCustomerPortalAnalyticsPayload({
      event: "offer_view",
      customer: customerQuery.data,
      fallbackProfile: {
        email: fallbackEmail,
        firstName: fallbackFirstName,
        lastName: fallbackLastName,
        phoneNumber: fallbackPhoneNumber,
      },
      offerType: transactionType,
      milesPointsType: program.name,
      amount_of_miles: program.sanitizedMiles,
      amount: program.totalPayout,
      bonusTier: program.loyaltyStatus,
    });

    pushCustomerPortalAnalyticsEvent({ payload });
    hasTrackedPageOpenRef.current = true;
  }, [
    customerId,
    customerQuery.data,
    customerQuery.isFetched,
    fallbackEmail,
    fallbackFirstName,
    fallbackLastName,
    fallbackPhoneNumber,
    program,
    transactionType,
  ]);

  if (!program) {
    return null;
  }

  if (isWaitingForCustomerContact) {
    return <SecureLoader />;
  }

  const resolveSellFingerprint = async () => {
    const freshFingerprintResult = await collectFingerprint();
    const freshFingerprint = parseFingerprintData({
      fingerprint: freshFingerprintResult.data,
    });

    if (freshFingerprint) {
      return freshFingerprint;
    }

    const sessionFingerprint = parseFingerprintData({
      fingerprint: fingerprintData,
    });

    if (sessionFingerprint) {
      return sessionFingerprint;
    }

    const temporaryDealFingerprint = parseFingerprintData({
      fingerprint: program.fingerprint,
    });

    if (temporaryDealFingerprint) {
      return temporaryDealFingerprint;
    }

    return undefined;
  };

  const onClick = async () => {
    if (isFormSubmitting) {
      return;
    }

    setSubmissionError(null);
    setIsFormSubmitting(true);

    try {
      if (transactionType === TransactionTypes.BUY) {
        const buyAccountDetails = accountDetails as Omit<
          z.infer<typeof buySubmissionFormEventDataSchema.shape.data>,
          "miles" | "averageRate" | "totalPayout"
        >;

        await functionsService.createBuyOrder({
          productId: program.id,
          customerId,
          loyaltyStatus: program.loyaltyStatus?.name ?? "",
          miles: program.sanitizedMiles,
          accountEmail: buyAccountDetails.accountEmail,
          firstName: buyAccountDetails.firstName,
          lastName: buyAccountDetails.lastName,
          frequentFlyerNumber: buyAccountDetails.frequentFlyerNumber,
          receivedMilesBefore: buyAccountDetails.receivedMilesBefore,
          notes: buyAccountDetails.notes ?? "",
        });
      }

      if (transactionType === TransactionTypes.SELL) {
        const sellAccountDetails = accountDetails as Omit<
          z.infer<typeof sellSubmissionFormEventDataSchema.shape.data>,
          "miles" | "averageRate" | "totalPayout"
        >;
        const sellFingerprint = await resolveSellFingerprint();

        await functionsService.createSellOrderFromTemporaryDeal({
          temporaryDealId: program.id,
          productId: program.programId,
          customerId,
          loyaltyStatus: program.loyaltyStatus?.name ?? "",
          miles: program.sanitizedMiles,
          milesExpirationDate: sellAccountDetails.milesExpirationDate,
          notes: sellAccountDetails.notes ?? "",
          accountPhoneNumber:
            customerContact.phoneNumber ||
            sellAccountDetails.accountPhoneNumber,

          accountPassword: sellAccountDetails.accountPassword ?? "",
          accountEmail:
            customerContact.email || sellAccountDetails.accountEmail,
          frequentFlyerNumber: sellAccountDetails.frequentFlyerNumber ?? "",
          paymentMethod: sellAccountDetails.paymentMethod,
          paypalEmail: sellAccountDetails.paypalEmail,
          ...(sellFingerprint ? { fingerprint: sellFingerprint } : {}),
        });
      }

      const trackingPayload = buildCustomerPortalAnalyticsPayload({
        event: "deal_add_payout_method",
        customer: customerQuery.data,
        fallbackProfile: {
          email: fallbackEmail,
          firstName: fallbackFirstName,
          lastName: fallbackLastName,
          phoneNumber: fallbackPhoneNumber,
        },
        offerType: transactionType,
        milesPointsType: program.name,
        amount_of_miles: program.sanitizedMiles,
        amount: program.totalPayout,
        payoutMethod:
          transactionType === TransactionTypes.SELL
            ? (
                accountDetails as Omit<
                  z.infer<typeof sellSubmissionFormEventDataSchema.shape.data>,
                  "miles" | "averageRate" | "totalPayout"
                >
              ).paymentMethod
            : null,
        bonusTier: program.loyaltyStatus,
      });

      pushCustomerPortalAnalyticsEvent({ payload: trackingPayload });

      savePendingDealSubmittedPayload({
        payload: buildCustomerPortalAnalyticsPayload({
          event: "deal_submitted",
          customer: customerQuery.data,
          fallbackProfile: {
            email: fallbackEmail,
            firstName: fallbackFirstName,
            lastName: fallbackLastName,
            phoneNumber: fallbackPhoneNumber,
          },
          offerType: transactionType,
          milesPointsType: program.name,
          amount_of_miles: program.sanitizedMiles,
          amount: program.totalPayout,
          payoutMethod:
            transactionType === TransactionTypes.SELL
              ? (
                  accountDetails as Omit<
                    z.infer<
                      typeof sellSubmissionFormEventDataSchema.shape.data
                    >,
                    "miles" | "averageRate" | "totalPayout"
                  >
                ).paymentMethod
              : null,
          bonusTier: program.loyaltyStatus,
          dealStatus: "successful",
        }),
      });

      navigate("/checkout-success");
    } catch (error) {
      setSubmissionError(
        error instanceof Error
          ? error.message
          : "We couldn't submit your order. Please try again.",
      );
    } finally {
      setIsFormSubmitting(false);
    }
  };
  return (
    <div className="relative flex flex-col gap-6 md:gap-0 w-full justify-between px-4 md:px-6 py-12 bg-[#063e9f]">
      {/* <div
        className={`absolute max-h-[1200px] top-0 left-0 w-full ${transactionType === TransactionTypes.SELL ? "h-[95%]" : "h-[100%] xl:h-[85%]"} bg-[#063e9f]`}
      /> */}

      <div
        className={`w-full max-w-[1500px] mx-auto flex justify-center flex-col xl:flex-row gap-6 xl:gap-12 pt-[2%]`}
      >
        <div
          className={`w-full flex flex-col items-center xl:w-[45%] gap-6 z-10`}
        >
          {/* Title and Subtitle Section */}
          <div
            className={`text-center xl:text-left relative ${
              transactionType === TransactionTypes.SELL ? "hidden xl:block" : ""
            }`}
          >
            {/* Background accent shape */}
            <div className="absolute -top-4 -left-4 w-32 h-32 bg-gradient-to-br from-blue-400/20 to-cyan-300/10 rounded-full blur-xl"></div>

            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 relative z-10">
              Just one more step to{" "}
              <span className="relative inline-block">
                <span className="relative bg-gradient-to-r from-white via-blue-100 to-blue-200 bg-clip-text text-transparent font-extrabold drop-shadow-[0_2px_8px_rgba(5,102,255,0.2)] filter contrast-110 brightness-105">
                  {transactionType === TransactionTypes.SELL
                    ? "get paid for your miles"
                    : "secure your miles"}
                </span>
              </span>
            </h1>
          </div>

          {/* <div className="block xl:hidden w-full">
            <Offer formRef={formRef} />
          </div> */}

          {/* {programs.length > 1 && (
            <div className="text-center mb-2">
              <span className="text-white text-2xl font-normal">
                {currentProgramIndex + 1} / {programs.length} Programs
              </span>
            </div>
          )} */}

          {/* <div className="w-full flex flex-col gap-4">
            {programs.slice(0, currentProgramIndex).map((program) => (
              <OrderSummary
                key={program.programId}
                program={program}
                asCompleted
              />
            ))} */}

          <OrderSummary
            program={program}
            showPayoutClaim={transactionType === TransactionTypes.SELL}
          />
          {/* {programs.slice(currentProgramIndex + 1).map((program) => (
              <OrderSummary
                key={program.programId}
                program={program}
                asUpcoming
              />
            ))} */}

          <div className="hidden xl:block">
            <SocialProof />
          </div>
        </div>
        <div className="flex flex-col gap-6 w-full xl:max-w-[60%] z-10">
          {/* <div className="hidden xl:block">
            <Offer formRef={formRef} />
          </div> */}
          <div ref={formRef} id="details-form">
            {submissionError ? (
              <Alert variant="destructive" className="mb-4">
                <AlertTitle>Submission failed</AlertTitle>
                <AlertDescription>{submissionError}</AlertDescription>
              </Alert>
            ) : null}
            {transactionType === TransactionTypes.SELL ? (
              <DetailsSellForm
                onContinueClick={onClick}
                programs={[program]}
                currentProgramIndex={0}
                isFormSubmitting={isFormSubmitting}
                customerEmail={customerContact.email}
                customerPhoneNumber={customerContact.phoneNumber}
              />
            ) : (
              <DetailsBuyForm
                onContinueClick={onClick}
                programs={[program]}
                currentProgramIndex={0}
                isFormSubmitting={isFormSubmitting}
              />
            )}
          </div>
        </div>

        <div className="block xl:hidden">
          <SocialProof isMobile />
        </div>
      </div>
    </div>
  );
}
